<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';
import type { Matter } from '../../stores/matterStore';

const props = defineProps<{ matter: Matter }>();
const emit = defineEmits<{ close: []; deleted: [] }>();

const { t } = useI18n();
const matterStore = useMatterStore();

// Type-to-confirm: the Delete button stays disabled until the typed value
// matches the matter name exactly (trimmed). A high-friction gate befitting an
// irreversible, data-destroying action.
const confirmText = ref('');
const errorMessage = ref<string | null>(null);

const canDelete = computed(
    () => confirmText.value.trim() === props.matter.Name && !matterStore.deleting,
);

// Reset transient state if the target matter changes under us.
watch(
    () => props.matter.RecordId,
    () => {
        confirmText.value = '';
        errorMessage.value = null;
    },
);

async function confirmDelete() {
    if (!canDelete.value) return;
    errorMessage.value = null;
    try {
        await matterStore.deleteMatter(props.matter.RecordId);
        emit('deleted');
    } catch (err) {
        errorMessage.value = err instanceof Error ? err.message : t('matters.delete.failed');
    }
}
</script>

<template>
    <div class="dm-overlay" @click.self="emit('close')">
        <div class="dm-modal">
            <div class="dm-modal-close-strip">
                <button class="dm-modal-close" type="button" @click="emit('close')">
                    <span class="material-icons" style="font-size: 18px">close</span>
                    <span>{{ t('common.close') }}</span>
                </button>
            </div>

            <div class="dm-modal-header">
                <span class="material-icons dm-modal-header-icon">warning</span>
                <span class="dm-modal-title">{{ t('matters.delete.title') }}</span>
            </div>

            <div class="dm-modal-body">
                <p class="dm-warning">{{ t('matters.delete.warning', { name: matter.Name }) }}</p>

                <label class="dm-confirm-label" for="dm-confirm-input">
                    {{ t('matters.delete.confirmLabel') }}
                </label>
                <input
                    id="dm-confirm-input"
                    v-model="confirmText"
                    type="text"
                    class="dm-confirm-input"
                    :placeholder="t('matters.delete.namePlaceholder')"
                    :disabled="matterStore.deleting"
                    autocomplete="off"
                    @keydown.enter="confirmDelete"
                />

                <div v-if="errorMessage" class="dm-error">
                    <span class="material-icons dm-error-icon">error_outline</span>
                    <span>{{ errorMessage }}</span>
                </div>
            </div>

            <div class="dm-modal-footer">
                <button
                    class="dm-footer-btn dm-footer-btn-secondary"
                    type="button"
                    :disabled="matterStore.deleting"
                    @click="emit('close')"
                >
                    {{ t('common.cancel') }}
                </button>
                <button
                    class="dm-footer-btn dm-footer-btn-danger"
                    type="button"
                    :disabled="!canDelete"
                    @click="confirmDelete"
                >
                    <span v-if="matterStore.deleting" class="dm-btn-spinner"></span>
                    <span v-else class="material-icons" style="font-size: 18px">delete_forever</span>
                    {{ matterStore.deleting ? t('matters.delete.deleting') : t('matters.delete.submit') }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.dm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: var(--z-modal-elevated);
    display: flex;
    align-items: center;
    justify-content: center;
}

.dm-modal {
    width: 460px;
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.dm-modal-close-strip {
    padding: 6px 14px;
    border-bottom: 1px solid var(--color-border);
}

.dm-modal-close {
    background: transparent;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--text-base);
    color: var(--color-text-primary);
    cursor: pointer;
    padding: 4px 8px;
}

.dm-modal-close:hover {
    color: var(--color-primary);
}

.dm-modal-header {
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid var(--color-border);
    background: var(--color-header-bg);
}

.dm-modal-header-icon {
    font-size: 20px;
    color: var(--color-error, #c62828);
}

.dm-modal-title {
    font-size: var(--text-base);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
}

.dm-modal-body {
    padding: 20px 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.dm-warning {
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    line-height: 1.5;
    margin: 0;
}

.dm-confirm-label {
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-text-primary);
}

.dm-confirm-input {
    padding: 8px 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    background: var(--color-bg-white);
}

.dm-confirm-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.dm-error {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--text-sm);
    color: var(--color-error, #c62828);
}

.dm-error-icon {
    font-size: 18px;
}

.dm-modal-footer {
    padding: 12px 20px 16px;
    border-top: 1px solid var(--color-border);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.dm-footer-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition: opacity 0.15s, background 0.15s;
}

.dm-footer-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.dm-footer-btn-secondary {
    background: var(--color-bg-white);
    color: var(--color-text-primary);
    border-color: var(--color-border);
}

.dm-footer-btn-secondary:not(:disabled):hover {
    background: var(--color-bg-light);
}

.dm-footer-btn-danger {
    background: var(--color-error, #c62828);
    color: #fff;
}

.dm-footer-btn-danger:not(:disabled):hover {
    opacity: 0.88;
}

.dm-btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: dm-spin 0.7s linear infinite;
    flex-shrink: 0;
}

@keyframes dm-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
